import { useEnvironment } from "../graphql/environment";
import { throttle } from "lodash";
import { useCallback } from "react";
import { hashCode, silenceTts, canTts, usePickVoices, canMultiLangTts, createTtsStreamUrl } from "./tts/ttsTools";
import { useGetState, useSetState } from "../components/ReduxProvider";
import { fetchTts } from "./tts/fetchTts";
import { fetchTtsLineByLine } from "./tts/fetchTtsLineByLine";
import { getTheme } from "styled/theme";
import { playAudio } from "./tts/playAudio";
import { useViewerQuery } from "./viewerQuery";
import { AssistantMode } from "components/assistant/__generated__/AssistantMutation.graphql";
export const TOO_LONG = 200;

type Args = {
  text: string;
  speakingRate?: number;
  voiceId?: string;
  hash?: string;
  onStarted?: (sound: Howl) => void;
  onEnded?: VoidFunction;
  useServerCache?: boolean;
};

export type ttsQueryOptions = {
  multiLangs?: string[];
  assistantMode?: AssistantMode;
};

export const useTtsQuery = (lang?: string, options?: ttsQueryOptions) => {
  const environment = useEnvironment();
  const lastVoice = useGetState("lastVoice");
  const setLastVoice = useSetState("lastVoice");
  const cardsData = useGetState("idbCardsData");
  const { viewer } = useViewerQuery();
  const ttsToken = viewer?.ttsToken;
  const { multiLangs, assistantMode } = options || {};
  const sortedVoices = usePickVoices(lang, lastVoice?.voiceId);
  const firstVoice = sortedVoices?.[0];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ttsQuery = useCallback(
    throttle(
      ({ text, speakingRate, voiceId: voiceIdParam, onEnded, onStarted, useServerCache = true }: Args) => {
        const voiceId = voiceIdParam ? voiceIdParam : firstVoice;
        const speakUp = (source: string) => {
          voiceId && setLastVoice({ voiceId, text });
          silenceTts();

          playAudio({
            src: source,
            onStarted,
            onEnded
          });

          const toHighlight = hashCode(text, voiceIdParam);
          const els = document.getElementsByClassName(toHighlight);
          for (let el of els as any) {
            el.style.color = getTheme().color.primary;
            setTimeout(() => el && el.style.removeProperty("color"), 1000);
          }
        };

        const cached =
          cardsData &&
          !speakingRate &&
          cardsData.find(
            (cd) => cd.text === text && cd.lang === lang && (voiceId === cd.ttsVoiceId || lastVoice?.text !== text)
          );

        if (cached) {
          cached.tts && speakUp(cached.tts);
        } else {
          const handleTtsResponse = (data: { speech?: string } | null) => {
            if (!data?.speech) {
              onEnded?.();
              return;
            }
            speakUp(data.speech);
          };

          const handleTtsError = (e: Error) => {
            onEnded?.();
            throw e;
          };

          if (assistantMode === "listenVocab" && multiLangs) {
            // Listening mode: Use line-by-line TTS (always cached for performance)
            fetchTtsLineByLine(environment, { text, targetLang: multiLangs[0], evenLineLang: multiLangs[1] || null })
              .then(handleTtsResponse)
              .catch(handleTtsError);
          } else if (multiLangs && canMultiLangTts(multiLangs)) {
            // Multi-language streaming - AI-assistant
            const streamUrl = createTtsStreamUrl(text, { speakingRate, token: ttsToken });
            speakUp(streamUrl);
          } else {
            // Single language standard TTS - cards
            fetchTts(environment, { lang: lang || "", text, speakingRate, voiceId, useCache: useServerCache })
              .then(handleTtsResponse)
              .catch(handleTtsError);
          }
        }
      },
      1000,
      { trailing: false }
    ),
    [firstVoice, cardsData, ttsToken]
  );

  if (multiLangs?.length && multiLangs.length > 0) {
    const isSingleDefaultLang = multiLangs.length === 1 && multiLangs[0] === lang;

    const isSupported =
      assistantMode === "listenVocab"
        ? multiLangs.every(canTts)
        : isSingleDefaultLang
          ? canTts(lang)
          : canMultiLangTts(multiLangs);

    return isSupported ? ttsQuery : null;
  }

  return canTts(lang) && cardsData ? ttsQuery : null;
};
